/**
 * Events & Sessions Page
 *
 * Same toolbar UX and visual shell as the Tracks page (search/sort/group,
 * column visibility, density, list/table toggle) — see TracksPage.tsx.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CalendarClock,
  Radio,
  ExternalLink,
  Users,
  Layers,
  ChevronRight,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { PageHeader as SharedPageHeader } from '../../../components/prioritization/PageHeader';
import { Button } from '../../../components/ui/Button';
import { useEntityListState } from '../../../hooks/useEntityListState';
import { EntityListToolbar } from '../../../components/shared/EntityListToolbar';
import { VirtualizedTable } from '../../../components/collection/VirtualizedTable';
import type { VirtualizedTableColumn } from '../../../components/collection/VirtualizedTable';
import { useSessionStore, useAllSessions } from '../../../stores/prioritization/sessionStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useTrackStore } from '../../../stores/prioritization/trackStore';
import { usePinnedCycleStore } from '../../../stores/prioritization/pinnedCycleStore';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { STEP_META } from '../../../features/session-engine-v3/types';
import { eventDesignerHref, sessionSurfaceOpenHref } from '../../../lib/catchballSessionLink';
import { SESSION_STATE_LABELS } from '@/lib/sessions/vocabulary';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useItemSpaceScope } from '../../../hooks/prioritization/useItemSpaceScope';
import { describeSessionSpaceScope, scopeSessionsToSpace } from '../../../lib/sessions/sessionSpaceScope';
import { sessionRetireAllowed } from '../../../lib/sessions/sessionRetireRight';
import { RetireSessionsDialog } from '../../../components/reversals/RetireSessionsDialog';

// ─── helpers ─────────────────────────────────────────────────────────────────

type V3Config = {
  primaryGoalStep?: string;
  containerMode?: 'single' | 'multi';
  participants?: Array<{ name: string }>;
  items?: Array<unknown>;
  steps?: Record<string, { required?: boolean; mode?: string; runStatus?: string; toolId?: string }>;
  /** stepId → child session id. The only record of Event→Session parentage:
   *  there is no parent column on prioritization_sessions. */
  childSessions?: Partial<Record<string, string>>;
};

function getV3Config(session: PrioritizationSession): V3Config | null {
  return (session.modeConfig as unknown as Record<string, unknown> | undefined)?.v3Config as V3Config | null ?? null;
}

function eventDesignerUrl(session: PrioritizationSession): string {
  return eventDesignerHref({
    resume: session.id,
    trackId: session.trackId,
    cycleId: session.cycleId,
  });
}

function liveUrl(session: PrioritizationSession): string {
  return `/prioritization/sessions/${session.id}/live`;
}

function isRunning(session: PrioritizationSession): boolean {
  const v3 = getV3Config(session);
  if (!v3?.steps) return false;
  return Object.values(v3.steps).some((s) => s.runStatus === 'running');
}

function goalStepLabel(v3: V3Config): string {
  const step = v3.primaryGoalStep;
  if (!step || !(step in STEP_META)) return '—';
  return `${step} · ${(STEP_META as Record<string, { label: string }>)[step]?.label ?? step}`;
}

function statusPill(session: PrioritizationSession): { label: string; cls: string } {
  const v3 = getV3Config(session);
  const running = isRunning(session);
  if (running) return { label: 'Live', cls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' };
  if (v3) {
    const allReq = v3.steps ? Object.entries(v3.steps).filter(([, s]) => s.required) : [];
    const allDone = allReq.every(([, s]) => s.runStatus === 'complete');
    if (allDone && allReq.length > 0) return { label: 'Complete', cls: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' };
    return { label: 'In progress', cls: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' };
  }
  const stateLabels: Record<string, { label: string; cls: string }> = {
    DRAFT:              { label: SESSION_STATE_LABELS.DRAFT, cls: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' },
    CRITERIA_FINALIZED: { label: SESSION_STATE_LABELS.CRITERIA_FINALIZED, cls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
    RUN_COMPLETE:       { label: SESSION_STATE_LABELS.RUN_COMPLETE, cls: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
    PUBLISHED:          { label: SESSION_STATE_LABELS.PUBLISHED, cls: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
  };
  return stateLabels[session.state ?? 'DRAFT'] ?? { label: session.state ?? '—', cls: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' };
}

/** Where a row's quick-action icon (external, opens in a new tab) should point. */
function quickActionHref(session: PrioritizationSession, isV3: boolean, running: boolean): string {
  if (running) return liveUrl(session);
  if (isV3) return eventDesignerUrl(session);
  return `/prioritization/sessions/${session.id}`;
}

function quickActionTitle(isV3: boolean, running: boolean): string {
  if (running) return 'Join live';
  if (isV3) return 'Open in Designer';
  return 'Open session';
}

// ─── row type ──────────────────────────────────────────────────────────────

interface SessionRow {
  id: string;
  name: string;
  isV3: boolean;
  /** The Event this sitting belongs to, rebuilt from v3Config.childSessions. */
  parentId: string | null;
  /** The S-steps this Event runs as their own sessions, in order. An Event is
   *  the parent of its S1–S5 step sessions; 0 or 1 means it is not a container. */
  childSteps: string[];
  typeLabel: 'Event' | 'Session';
  goalStep: string;
  status: string;
  statusCls: string;
  participantCount: number;
  itemCount: number;
  isRunning: boolean;
  createdAt: Date | undefined;
  cycleId: string;
  cycleName: string;
  trackName: string;
  _session: PrioritizationSession;
}

// ─── column / sort / filter / group definitions ─────────────────────────────

const ALL_COLUMNS = [
  { key: 'name',             label: 'Name',    defaultVisible: true },
  { key: 'typeLabel',        label: 'Type',    defaultVisible: true },
  { key: 'status',           label: 'Status',  defaultVisible: true },
  { key: 'goalStep',         label: 'Goal',    defaultVisible: true },
  { key: 'participantCount', label: 'People',  defaultVisible: true },
  { key: 'itemCount',        label: 'Items',   defaultVisible: false },
  { key: 'createdAt',        label: 'Created', defaultVisible: true },
];

const SORT_OPTIONS = [
  { field: 'createdAt', label: 'Date created' },
  { field: 'name',      label: 'Name' },
  { field: 'status',    label: 'Status' },
  { field: 'typeLabel', label: 'Type' },
];

const GROUP_OPTIONS = [
  { field: 'typeLabel', label: 'Type' },
  { field: 'status',    label: 'Status' },
];

const FILTER_FIELDS = [
  {
    key: 'typeLabel',
    label: 'Type',
    options: [
      { value: 'Event',   label: 'Events' },
      { value: 'Session', label: 'Sessions' },
    ],
  },
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'Live',        label: 'Live' },
      { value: 'In progress', label: 'In progress' },
      { value: 'Complete',    label: 'Complete' },
      { value: 'Draft',       label: 'Draft' },
      { value: 'Set up',      label: 'Set up' },
      { value: 'Done',        label: 'Done' },
      { value: 'Published',   label: 'Published' },
    ],
  },
];

const DEFAULT_SORT_FIELD = 'createdAt';
const DEFAULT_SORT_DIR = 'desc' as const;

// ─── filtering + sorting helpers ─────────────────────────────────────────────

function matchesSearch(row: SessionRow, q: string): boolean {
  if (!q) return true;
  const lower = q.toLowerCase();
  return row.name.toLowerCase().includes(lower) ||
    row.goalStep.toLowerCase().includes(lower) ||
    row.cycleName.toLowerCase().includes(lower) ||
    row.trackName.toLowerCase().includes(lower) ||
    row.status.toLowerCase().includes(lower) ||
    row.typeLabel.toLowerCase().includes(lower);
}

function sortRows(rows: SessionRow[], field: string, dir: 'asc' | 'desc'): SessionRow[] {
  return [...rows].sort((a, b) => {
    let valA: string | number = '';
    let valB: string | number = '';
    if (field === 'name')      { valA = a.name; valB = b.name; }
    else if (field === 'status')    { valA = a.status; valB = b.status; }
    else if (field === 'typeLabel') { valA = a.typeLabel; valB = b.typeLabel; }
    else if (field === 'createdAt') { valA = a.createdAt?.getTime() ?? 0; valB = b.createdAt?.getTime() ?? 0; }
    if (valA < valB) return dir === 'asc' ? -1 : 1;
    if (valA > valB) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

// ─── main page ────────────────────────────────────────────────────────────────

export default function EventsAndSessionsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const allSessions = useAllSessions();
  const syncState = useSessionStore((s) => s.syncState);
  const fetchFromSupabase = useSessionStore((s) => s.fetchFromSupabase);
  const cycles = useDegapProcessStore((s) => s.cycles);
  const tracks = useTrackStore((s) => s.tracks);

  const userId = useUserStore((s) => s.userId);
  const can = useUserStore((s) => s.can);
  // The current space decides which sessions are listed (lib/sessions/sessionSpaceScope.ts):
  // a Mandate space lists only Mandate sessions, a personal space only your own.
  const spaceScope = useItemSpaceScope();
  const [showAllSpaces, setShowAllSpaces] = useState(false);
  const sessions = useMemo(
    () => scopeSessionsToSpace(allSessions, spaceScope, userId, { showAllSpaces }),
    [allSessions, spaceScope, userId, showAllSpaces],
  );
  const spaceWords = describeSessionSpaceScope(spaceScope, { showAllSpaces });
  // Retire is the governed "delete" (retire_session, migration 758). Drawn only
  // for the facilitator, the creator or a workspace admin (build contract §2.8).
  const [retireTarget, setRetireTarget] = useState<PrioritizationSession | null>(null);
  // Creating a session inserts into prioritization_sessions, whose insert rule
  // (migration 754) takes the SESSION_RUN capability: session.facilitate or
  // session.record on the client. New Session is drawn only for them (§2.8).
  const mayCreate = can('session.facilitate') || can('session.record');
  const mayRetire = (session: PrioritizationSession) =>
    sessionRetireAllowed({ userId, facilitatorId: session.facilitatorId, createdBy: session.createdBy, can });

  const urlCycleId = searchParams.get('cycleId');
  const pinnedCycleId = usePinnedCycleStore((s) => s.pinnedCycleId);
  const pinnedCycleName = usePinnedCycleStore((s) => s.pinnedCycleName);
  const activeCycleId = urlCycleId ?? pinnedCycleId;

  // Sessions are workspace-scoped; fetch the full set on mount (same pattern
  // as ItemListView / KernelList) rather than depending on whichever other
  // page happened to hydrate the store first.
  useEffect(() => {
    void fetchFromSupabase();
  }, [fetchFromSupabase]);

  const cycleNameById = useMemo(
    () => Object.fromEntries(cycles.map((c) => [c.id, c.name ?? ''])),
    [cycles],
  );
  const trackNameById = useMemo(
    () => Object.fromEntries(tracks.map((t) => [t.id, t.name ?? ''])),
    [tracks],
  );

  const activeCycleName = useMemo(
    () => cycles.find((c) => c.id === activeCycleId)?.name ?? pinnedCycleName ?? 'Selected cycle',
    [cycles, activeCycleId, pinnedCycleName],
  );

  const allRows: SessionRow[] = useMemo(
    () => {
      // Cycle → Track → Event → Session is the intended IA, but only the first
      // two hops are real: tracks.cycle_id and prioritization_sessions.track_id
      // are foreign keys, while an Event is itself a session row whose children
      // live in a `childSessions` map inside mode_config.v3Config. Nothing in
      // the schema says "this session belongs to that event", so this list used
      // to render a parent and its own child sittings as indistinguishable
      // PEER ROWS — the reason the hierarchy was invisible here.
      //
      // Rebuild the missing edge from the blob the rows already carry.
      const parentOf = new Map<string, string>();
      const stepsOf = new Map<string, string[]>();
      const stepOfChild = new Map<string, string>();

      // Migration 694 made parentage a real column. Prefer it — it points from
      // the child, so it needs no scan and cannot disagree with itself. The blob
      // pass below still runs for rows written before the column existed.
      for (const s of sessions) {
        const row = s as unknown as { parentSessionId?: string | null; parent_session_id?: string | null; parentStepId?: string | null; parent_step_id?: string | null };
        const parentId = row.parentSessionId ?? row.parent_session_id ?? null;
        const stepId = row.parentStepId ?? row.parent_step_id ?? null;
        if (parentId) {
          parentOf.set(s.id, parentId);
          if (stepId) stepOfChild.set(s.id, stepId);
          stepsOf.set(parentId, [...(stepsOf.get(parentId) ?? []), stepId ?? '?'].sort());
        }
      }

      for (const s of sessions) {
        const kids = Object.entries(getV3Config(s)?.childSessions ?? {}).filter(
          (e): e is [string, string] => typeof e[1] === 'string' && e[1].length > 0,
        );
        // childSessions is keyed by step id, so the parent already knows which
        // of S1–S5 it broke out into its own sitting — say that, rather than an
        // anonymous count.
        // Column wins; only fill gaps it did not cover.
        if (!stepsOf.has(s.id) && kids.length > 0) {
          stepsOf.set(s.id, kids.map(([step]) => step).sort());
        }
        for (const [step, kid] of kids) {
          if (parentOf.has(kid)) continue;
          parentOf.set(kid, s.id);
          stepOfChild.set(kid, step);
        }
      }

      return sessions.map((s) => {
        const v3 = getV3Config(s);
        const pill = statusPill(s);
        const isV3 = !!v3;
        const trackId = (s as unknown as { trackId?: string }).trackId;
        const childSteps = stepsOf.get(s.id) ?? [];
        return {
          id: s.id,
          name: s.name ?? 'Untitled',
          isV3,
          parentId: parentOf.get(s.id) ?? null,
          childSteps,
          // The container layer earns a name only when it holds more than one
          // sitting. A single-session event is not a parent of anything — call
          // it what it is, and stop charging the reader for a layer that has
          // one child (owner ruling 2026-08-31).
          typeLabel: isV3 && childSteps.length > 1 ? 'Event' : 'Session',
          goalStep: stepOfChild.get(s.id) ?? (v3 ? goalStepLabel(v3) : (s.selectedToolId ?? '—')),
          status: pill.label,
          statusCls: pill.cls,
          participantCount: v3?.participants?.length ?? 0,
          itemCount: (v3?.items as unknown[] | undefined)?.length ?? 0,
          isRunning: isRunning(s),
          createdAt: s.createdAt ? new Date(s.createdAt) : undefined,
          cycleId: s.cycleId ?? '',
          cycleName: s.cycleId ? (cycleNameById[s.cycleId] ?? '') : '',
          trackName: trackId ? (trackNameById[trackId] ?? '') : '',
          _session: s,
        };
      });
    },
    [sessions, cycleNameById, trackNameById],
  );

  // Pre-filter by active cycle (URL param / pinned), same as TracksPage.
  // Child sittings are reached by opening their Event — listing them alongside
  // it repeats the same work twice at the same level and hides the structure.
  const baseRows = useMemo(() => {
    const topLevel = allRows.filter((r) => !r.parentId);
    return activeCycleId ? topLevel.filter((r) => r.cycleId === activeCycleId) : topLevel;
  }, [allRows, activeCycleId]);

  const listState = useEntityListState({
    allColumns: ALL_COLUMNS,
    defaultSortField: DEFAULT_SORT_FIELD,
    defaultSortDirection: DEFAULT_SORT_DIR,
    storageKey: 'sessions',
    filterFields: FILTER_FIELDS,
  });

  const filtered = useMemo(() => {
    let result = baseRows;
    if (listState.searchQuery) result = result.filter((r) => matchesSearch(r, listState.searchQuery));
    result = listState.applyFilters(result, (row, field) => {
      if (field === 'typeLabel') return row.typeLabel;
      if (field === 'status') return row.status;
      return undefined;
    });
    result = sortRows(result, listState.sort.field, listState.sort.direction);
    return result;
    // Pre-existing: the deps intentionally name the three fields this memo reads
    // rather than the whole `listState` object, which changes identity on every
    // render. Silenced because editing this file brings it into CI's changed-file
    // lint at --max-warnings=0.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRows, listState.searchQuery, listState.sort, listState.applyFilters]);

  const groupedItems = useMemo(() => {
    if (!listState.groupBy) return null;
    const groups: Map<string, SessionRow[]> = new Map();
    for (const row of filtered) {
      const key = listState.groupBy === 'typeLabel' ? row.typeLabel : row.status;
      const arr = groups.get(key) ?? [];
      arr.push(row);
      groups.set(key, arr);
    }
    return groups;
  }, [filtered, listState.groupBy]);

  const rowHeight = listState.density === 'compact' ? 40 : listState.density === 'comfortable' ? 72 : 56;

  const openRow = (row: SessionRow) => navigate(sessionSurfaceOpenHref(row._session));

  const tableColumns = useMemo((): VirtualizedTableColumn<SessionRow>[] => {
    const colMap: Record<string, VirtualizedTableColumn<SessionRow>> = {
      name: {
        key: 'name', label: 'Name', width: '30%', sortable: true,
        render: (row) => (
          <div className="flex items-center gap-2 min-w-0">
            <div className="min-w-0 flex-1">
              <span className="text-sm font-medium text-[var(--text-pri)] truncate block">{row.name}</span>
              {(row.cycleName || row.trackName) && listState.density !== 'compact' && (
                <p className="text-xs text-[var(--text-ter)] truncate mt-0.5">{[row.cycleName, row.trackName].filter(Boolean).join(' · ')}</p>
              )}
            </div>
            {mayRetire(row._session) && (
              <RetireRowButton name={row.name} onClick={() => setRetireTarget(row._session)} />
            )}
          </div>
        ),
      },
      typeLabel: {
        key: 'typeLabel', label: 'Type', width: '10%', sortable: true,
        render: (row) => (
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${row.isV3 ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
            {row.typeLabel}
            {row.childSteps.length > 1 && (
              <span style={{ marginLeft: 6, opacity: 0.7 }}>· {row.childSteps.join(' ')}</span>
            )}
          </span>
        ),
      },
      status: {
        key: 'status', label: 'Status', width: '13%', sortable: true,
        render: (row) => (
          <div className="flex items-center gap-1.5">
            {row.isRunning && <Radio className="h-3 w-3 text-[var(--mustard)] animate-pulse" />}
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${row.statusCls}`}>{row.status}</span>
          </div>
        ),
      },
      goalStep: {
        key: 'goalStep', label: 'Goal', width: '19%',
        render: (row) => <span className="text-xs text-[var(--text-ter)] truncate block">{row.goalStep}</span>,
      },
      participantCount: {
        key: 'participantCount', label: 'People', width: '9%',
        render: (row) => (
          <span className="inline-flex items-center gap-1.5 text-sm text-[var(--text-sec)]"><Users className="h-3.5 w-3.5 text-[var(--text-ter)]" />{row.participantCount}</span>
        ),
      },
      itemCount: {
        key: 'itemCount', label: 'Items', width: '9%',
        render: (row) => (
          <span className="inline-flex items-center gap-1.5 text-sm text-[var(--text-sec)]"><Layers className="h-3.5 w-3.5 text-[var(--text-ter)]" />{row.itemCount}</span>
        ),
      },
      createdAt: {
        key: 'createdAt', label: 'Created', width: '10%', sortable: true,
        render: (row) => <span className="text-sm text-[var(--text-ter)]">{row.createdAt ? row.createdAt.toLocaleDateString() : '—'}</span>,
      },
    };
    return listState.visibleColumns.map((key) => colMap[key]).filter(Boolean) as VirtualizedTableColumn<SessionRow>[];
    // mayRetire reads userId and can, both listed; the function itself is rebuilt each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listState.visibleColumns, listState.density, userId, can]);

  const renderListRow = (row: SessionRow) => {
    const href = quickActionHref(row._session, row.isV3, row.isRunning);
    const title = quickActionTitle(row.isV3, row.isRunning);
    return (
      <div
        key={row.id}
        onClick={() => openRow(row)}
        className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-[var(--surface)]/60"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-[var(--text-pri)] truncate">{row.name}</span>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${row.isV3 ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
              {row.typeLabel}
            </span>
            {row.isRunning && <Radio className="h-3 w-3 text-[var(--mustard)] animate-pulse" />}
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${row.statusCls}`}>{row.status}</span>
          </div>
          <div className="mt-0.5 flex items-center gap-3 text-xs text-[var(--text-ter)] flex-wrap">
            {(row.cycleName || row.trackName) && <span>{[row.cycleName, row.trackName].filter(Boolean).join(' · ')}</span>}
            <span className="flex items-center gap-1"><Users className="h-3 w-3" />{row.participantCount}</span>
            <span className="flex items-center gap-1"><Layers className="h-3 w-3" />{row.itemCount} items</span>
            <span>{row.createdAt ? row.createdAt.toLocaleDateString() : '—'}</span>
          </div>
        </div>
        {mayRetire(row._session) && (
          <RetireRowButton name={row.name} onClick={() => setRetireTarget(row._session)} />
        )}
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          title={title}
          className="flex-shrink-0 p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
        <ChevronRight className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
      </div>
    );
  };

  const renderGroup = (groupKey: string, items: SessionRow[], mode: 'list' | 'table') => (
    <div key={groupKey}>
      <div
        className="flex items-center gap-2 px-4 py-2 bg-[var(--surface)] border-b border-[var(--rule)] cursor-pointer hover:bg-[var(--hover-tint)] transition-colors"
        onClick={() => listState.toggleGroup(groupKey)}
      >
        <ChevronRight className={`h-3.5 w-3.5 text-[var(--text-ter)] transition-transform ${listState.isGroupOpen(groupKey) ? 'rotate-90' : ''}`} />
        <span className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide">{groupKey || 'Unlabeled'}</span>
        <span className="text-xs text-[var(--text-ter)] ml-1">{items.length}</span>
      </div>
      {listState.isGroupOpen(groupKey) && (
        mode === 'table' ? (
          <VirtualizedTable
            items={items} getId={(r) => r.id} columns={tableColumns} rowHeight={rowHeight}
            selectedIds={new Set()} onToggle={() => {}} selectable={false}
            onRowClick={openRow}
            currentSort={{ field: listState.sort.field as never, direction: listState.sort.direction }}
            onSortChange={(field) => listState.toggleSort(field as string)}
            height={Math.min(items.length * rowHeight + 40, 600)}
          />
        ) : (
          <div className="divide-y divide-[var(--rule)]">{items.map(renderListRow)}</div>
        )
      )}
    </div>
  );

  // Count what the rows actually say. `isV3` is true for every session the v3
  // engine created, including one-sitting ones that are no longer presented as
  // containers — counting those as "events" contradicted the labels below it.
  const eventCount = useMemo(
    () => baseRows.filter((r) => r.typeLabel === 'Event').length,
    [baseRows],
  );
  const liveCount = useMemo(() => baseRows.filter((r) => r.isRunning).length, [baseRows]);

  return (
    <div className="space-y-4">
      <SharedPageHeader
        icon={<CalendarClock className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Events & Sessions"
        subtitle={`${baseRows.length} total · ${eventCount} event${eventCount !== 1 ? 's' : ''} · ${liveCount} live now`}
        actions={
          <div className="flex items-center gap-3">
            {/* The "Legacy view" escape hatch to ?view=admin was removed 2026-08-27
                (DEP-027). It offered a third listing of the same sessions with no
                stated reason to prefer it. */}
            {mayCreate && (
              // Narrowed to a cycle, a new session is that cycle's: the Designer relates it without asking.
              <Button variant="primary" onClick={() => navigate(eventDesignerHref({ cycleId: activeCycleId }))}>
                <Plus className="h-4 w-4" />New Session
              </Button>
            )}
          </div>
        }
      />

      {/* Which space the list is narrowed to, as the Items list says it */}
      {spaceWords && (
        <div className="flex items-center gap-2 text-[11px] text-[var(--text-ter)]" data-testid="sessions-space-scope">
          <span>{spaceWords.label}</span>
          <button
            type="button"
            onClick={() => setShowAllSpaces((v) => !v)}
            className="font-medium text-[var(--text-sec)] underline-offset-2 hover:underline"
          >
            {spaceWords.toggleLabel}
          </button>
        </div>
      )}

      {/* Active cycle filter chip */}
      {activeCycleId && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--mustard)]" />
            Filtered to: <span className="font-semibold">{activeCycleName}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSearchParams((p) => { p.delete('cycleId'); return p; })}
              aria-label="Clear cycle filter"
              className="!ml-1 !p-0.5 !text-[var(--mustard)] hover:!text-[var(--mustard)] hover:!bg-transparent"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </span>
        </div>
      )}

      <EntityListToolbar
        searchQuery={listState.searchQuery}
        onSearchChange={listState.setSearchQuery}
        searchPlaceholder="Search events and sessions…"
        filterFields={FILTER_FIELDS}
        activeFilters={listState.activeFilters}
        onFilterChange={listState.setFieldFilter}
        onClearFilters={listState.clearAllFilters}
        filterCount={listState.filterCount}
        filterPanelOpen={listState.filterPanelOpen}
        onToggleFilterPanel={listState.toggleFilterPanel}
        sort={listState.sort}
        onSortChange={listState.setSort}
        sortOptions={SORT_OPTIONS}
        groupBy={listState.groupBy}
        onGroupByChange={listState.setGroupBy}
        groupOptions={GROUP_OPTIONS}
        allColumns={ALL_COLUMNS}
        visibleColumns={listState.visibleColumns}
        onToggleColumn={listState.toggleColumn}
        onReorderColumns={listState.reorderColumns}
        density={listState.density}
        onDensityChange={listState.setDensity}
        viewMode={listState.viewMode}
        onViewModeChange={listState.setViewMode}
        totalCount={baseRows.length}
        filteredCount={filtered.length}
      />

      {/* Content */}
      {syncState.isLoading && allRows.length === 0 ? (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-12 text-center">
          <p className="text-sm text-[var(--text-ter)]">Loading Events &amp; Sessions…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-12 text-center">
          <p className="text-sm font-medium text-[var(--text-sec)] mb-1">
            {listState.searchQuery ? 'No events or sessions match your search' : 'No events or sessions yet'}
          </p>
          <p className="text-xs text-[var(--text-ter)]">
            {listState.searchQuery
              ? 'Try a different search term.'
              : mayCreate
                ? 'Create a session to start a structured prioritization run.'
                : 'Sessions you are part of will be listed here. Starting one takes the Facilitate Sessions or Record Decisions permission.'}
          </p>
        </div>
      ) : listState.viewMode === 'table' ? (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
          {groupedItems
            ? Array.from(groupedItems.entries()).map(([k, items]) => renderGroup(k, items, 'table'))
            : (
              <VirtualizedTable
                items={filtered} getId={(r) => r.id} columns={tableColumns} rowHeight={rowHeight}
                selectedIds={new Set()} onToggle={() => {}} selectable={false}
                onRowClick={openRow}
                currentSort={{ field: listState.sort.field as never, direction: listState.sort.direction }}
                onSortChange={(field) => listState.toggleSort(field as string)}
                height={Math.min(filtered.length * rowHeight + 40, 700)}
              />
            )}
        </div>
      ) : (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden divide-y divide-[var(--rule)]">
          {groupedItems
            ? Array.from(groupedItems.entries()).map(([k, items]) => renderGroup(k, items, 'list'))
            : filtered.map(renderListRow)}
        </div>
      )}

      <RetireSessionsDialog
        open={retireTarget !== null}
        onClose={() => setRetireTarget(null)}
        sessions={retireTarget ? [{ id: retireTarget.id, name: retireTarget.name }] : []}
      />
    </div>
  );
}

/** The row's Retire control — drawn only when sessionRetireAllowed says yes. */
function RetireRowButton({ name, onClick }: { name: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title="Retire session"
      aria-label={`Retire ${name}`}
      className="flex-shrink-0 p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}
